import React, { useState, useEffect } from "react";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useSubscriptionApi } from "src/_common/hooks/actions/subscription/appSubscriptionApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import SubscriptionPlansGrid from "../settings/subscriptions/SubscriptionPlansGrid";

interface UpgradePlanModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const UpgradePlanModal: React.FC<UpgradePlanModalProps> = ({ isVisible, onClose }) => {
  const userApi = useUserApi();
  const subscriptionApi = useSubscriptionApi();
  const toast = useToaster();
  const userSelector = useAppUserDetailsSelector();
  const { t } = useTranslation("payment");
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();

  const [currentPlanData, setCurrentPlanData] = useState<any>(null);
  const [subscriptionPlanData, setSubscriptionPlanData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("yearly");

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  const canCreateBillingInfo = canAccessMenu("create_billing_info");

  // Fetch subscription plans from API
  useEffect(() => {
    if (isVisible) {
      loadSubscriptionPlans();
    }
  }, [isVisible]);

  const loadSubscriptionPlans = () => {
    setIsLoading(true);
    subscriptionApi.callGetSubscriptionPlans(
      {},
      (message: string, response: any) => {
        setIsLoading(false);
        setSubscriptionPlanData(response || null);
        setCurrentPlanData(response.current_plan || null);
      },
      (error: any) => {
        console.error("Failed to load subscription plans:", error);
        setIsLoading(false);
      }
    );
  };

  const handlePlanUpgradeSuccess = () => {
    // Close modal and refresh data
    toast.success(t("subscriptionPlans.upgradeModal.successMessage"));
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        onClose();
      }}
    >
      <div className="upgrade-plan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title">{t("subscriptionPlans.upgradeModal.title")}</h2>
            <p className="modal-subtitle">
              {t("subscriptionPlans.upgradeModal.subtitle")}
            </p>
          </div>
          <button className="modal-close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-content">
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <div className="toggle-switch-wrapper">
              <span className={`toggle-label ${billingPeriod === "monthly" ? "active" : ""}`}>{t("subscriptionPlans.billingPeriod.monthly")}</span>
              <div
                className="toggle-switch"
                onClick={() => setBillingPeriod(billingPeriod === "monthly" ? "yearly" : "monthly")}
              >
                <div className={`toggle-slider ${billingPeriod === "yearly" ? "yearly" : "monthly"}`}></div>
              </div>
              <span className={`toggle-label ${billingPeriod === "yearly" ? "active" : ""}`}>{t("subscriptionPlans.billingPeriod.yearly")}</span>
            </div>
          </div>

          {/* Use the shared SubscriptionPlansGrid component */}
          <SubscriptionPlansGrid
            subscriptionPlanData={subscriptionPlanData}
            currentPlanData={currentPlanData}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            billingPeriod={billingPeriod}
            onPlanUpgradeSuccess={handlePlanUpgradeSuccess}
            showCurrentPlan={true}
            onLoadSubscriptionPlans={loadSubscriptionPlans}
          />
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <p className="footer-text">
            {t("subscriptionPlans.upgradeModal.footer.customPlanText")}{" "}
            <a href="mailto:support@drjobs.com" className="footer-link">
              {t("subscriptionPlans.upgradeModal.footer.contactSales")}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradePlanModal;
